import { useState, useEffect, useRef } from 'react';

interface PlaceHoursResult {
  hours: string[];
  isOpen: boolean | null;
  loading: boolean;
}

const FALLBACK_HOURS = ["Mon–Sat: 9:00 AM – 6:00 PM", "Sun: Closed"];

export const useGooglePlaceHours = (query: string): PlaceHoursResult => {
  const [hours, setHours] = useState<string[]>(FALLBACK_HOURS);
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!window.google?.maps?.places) {
      setLoading(false);
      return;
    }

    // PlacesService needs a DOM element
    if (!containerRef.current) {
      containerRef.current = document.createElement('div');
      containerRef.current.style.display = 'none';
      document.body.appendChild(containerRef.current);
    }

    const service = new google.maps.places.PlacesService(containerRef.current);

    service.findPlaceFromQuery(
      { query, fields: ['place_id'] },
      (results, status) => {
        if (
          status !== google.maps.places.PlacesServiceStatus.OK ||
          !results?.[0]?.place_id
        ) {
          setLoading(false);
          return;
        }

        service.getDetails(
          { placeId: results[0].place_id, fields: ['opening_hours'] },
          (place, detailStatus) => {
            if (
              detailStatus === google.maps.places.PlacesServiceStatus.OK &&
              place?.opening_hours
            ) {
              const weekday = place.opening_hours.weekday_text;
              if (weekday && weekday.length > 0) {
                setHours(weekday);
              }
              if (typeof place.opening_hours.isOpen === 'function') {
                try {
                  setIsOpen(place.opening_hours.isOpen());
                } catch {
                  // ignore
                }
              }
            }
            setLoading(false);
          }
        );
      }
    );

    return () => {
      if (containerRef.current && containerRef.current.parentNode) {
        containerRef.current.parentNode.removeChild(containerRef.current);
        containerRef.current = null;
      }
    };
  }, [query]);

  return { hours, isOpen, loading };
};
